import { AlignmentQueryKeys } from "@/api/alignment/alignment.queries";
import type { RouterContextType } from "@/api/permissions/permissions.api";
import AlignmentTabStep from "@/components/features/AlignmentSteps/AlignmentTabStep/AlignmentTabStep";
import Coverage from "@/components/features/AlignmentSteps/Coverage/Coverage";
import { AlignmentInfoSection } from "@/components/features/AlignmentSteps/InfoSections/AlignmentInfoSection";
import { AlignmentRanking } from "@/components/features/AlignmentSteps/Ranking/AlignmentRanking";
import { AlignmentRates } from "@/components/features/AlignmentSteps/Rates";
import Trades from "@/components/features/AlignmentSteps/Trades/AlignmentTrades";
import { buildW2SummaryData } from "@/components/features/SummaryComponent/buildW2SummaryData";
import SummaryComponent from "@/components/features/SummaryComponent/SummaryComponent";
import { toSummaryNote } from "@/components/features/SummaryComponent/toSummaryNote";
import {
  useCreateAlignmentNote,
  useUpdateAlignmentNote,
} from "@/hooks/alignment/useAlignmentMutations";
import { useAlignmentNotes } from "@/hooks/alignment/useAlignmentQueries";
import { useServiceProviderJsonForm } from "@/hooks/service-provider/useServiceProviderQueries";
import { handleApiSuccess, handleApiError } from "@/utils/responseHandler";
import type { ApiError } from "@/types/common.types";
import { authGuardWithPermissions } from "@/utils/authGuard";
import { Stack, Text } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

export const Route = createFileRoute(
  "/service-providers/$id/alignment/_alignmentLayout/$providerType/",
)({
  component: AlignmentPage,
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
});

const SECTION_TO_NOTE_TYPE: Record<string, "accoree_internal" | "company_wide_internal"> = {
  accoreeInternalNotes: "accoree_internal",
  companyWideInternalNotes: "company_wide_internal",
};

const SUMMARY_SECTION_TO_PROFILE_TAB = {
  ownerInformation: "owner-info",
  applicantInformation: "applicant-info",
  businessInformation: "business-info",
  trades: "trades",
  rates: "rates",
  payments: "payments",
  coverage: "coverage",
  compensation: "compensation",
  licensesAndCertifications: "license-and-certificates",
  vehicleUniforms: "vehicle",
} as const;

/*
 * SERV-3337: a section only gets a label when the card beneath it is not already named for the
 * section and the stepper step does not name it either. Applicant and Owner Information repeat
 * the stepper step title, and the cards under Trades, Rates, Coverage, Inclusions & Exclusions
 * and Ranking all name themselves, so none of those carry a label. The detail tab already took
 * this approach for its rules card.
 */
function AlignmentSection({ title, children }: { title?: string; children: React.ReactNode }) {
  return (
    <Stack gap="var(--spacing-medium)" w="100%">
      {title && (
        <Text
          fw="var(--font-weight-bold)"
          fz="var(--heading-font-size-h5)"
          lh="var(--heading-line-height-h5)"
          c="var(--text-strong)"
        >
          {title}
        </Text>
      )}
      {children}
    </Stack>
  );
}

function InternalNotesSection({
  serviceProviderId,
  providerType,
}: {
  serviceProviderId: string;
  providerType: string;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { data: alignmentNotesData } = useAlignmentNotes(serviceProviderId);
  const { data: summaryData } = useServiceProviderJsonForm("summary", serviceProviderId);
  const { mutate: createNote, isPending: isCreateNotePending } =
    useCreateAlignmentNote(serviceProviderId);
  const { mutate: updateNote, isPending: isUpdateNotePending } =
    useUpdateAlignmentNote(serviceProviderId);

  const notesByType = (noteType: "accoree_internal" | "company_wide_internal") =>
    alignmentNotesData?.data?.notes
      ?.filter((note) => note.noteType === noteType)
      .map(toSummaryNote) ?? [];

  const allAlignmentNotes = {
    accoreeInternalNotes: notesByType("accoree_internal"),
    companyWideInternalNotes: notesByType("company_wide_internal"),
  };

  const latestNotes = {
    accoreeInternalNotes: allAlignmentNotes.accoreeInternalNotes.slice(0, 1),
    companyWideInternalNotes: allAlignmentNotes.companyWideInternalNotes.slice(0, 1),
  };

  const invalidateNotes = () => {
    queryClient.invalidateQueries({
      queryKey: [AlignmentQueryKeys.alignmentNotes, serviceProviderId],
    });
  };

  const handleAddNote = (sectionKey: string, note: string, onSuccess?: () => void) => {
    const noteType = SECTION_TO_NOTE_TYPE[sectionKey];
    if (!noteType) return;
    createNote(
      { noteText: note, noteType },
      {
        onSuccess: () => {
          invalidateNotes();
          handleApiSuccess("Alignment note created successfully");
          onSuccess?.();
        },
        onError: (error) => {
          handleApiError(error as ApiError);
        },
      },
    );
  };

  const handleEditNote = (
    sectionKey: string,
    noteId: string,
    note: string,
    onSuccess?: () => void,
  ) => {
    const noteType = SECTION_TO_NOTE_TYPE[sectionKey];
    if (!noteType) return;
    // Alignment note ids are uuids. Coercing one with `+` gave NaN, which serialised as null and
    // made the upsert CREATE a second note instead of editing this one (SERV-2839).
    updateNote(
      { id: noteId, noteText: note, noteType },
      {
        onSuccess: () => {
          invalidateNotes();
          handleApiSuccess("Alignment note updated successfully");
          onSuccess?.();
        },
        onError: (error) => {
          handleApiError(error as ApiError);
        },
      },
    );
  };

  const handleGoToTab = (sectionKey: string) => {
    const tab =
      SUMMARY_SECTION_TO_PROFILE_TAB[sectionKey as keyof typeof SUMMARY_SECTION_TO_PROFILE_TAB];
    navigate({
      to: "/service-providers/$id",
      params: { id: serviceProviderId },
      ...(tab && { search: { tab } }),
    });
  };

  return (
    <SummaryComponent
      summaryData={
        providerType === "w2" ? buildW2SummaryData(summaryData ?? {}) : (summaryData ?? {})
      }
      notesData={latestNotes}
      allAlignmentNotes={allAlignmentNotes}
      handleGoToTab={handleGoToTab}
      showNotesCard={true}
      onAddNote={handleAddNote}
      onEditNote={handleEditNote}
      onOpenNotesModal={invalidateNotes}
      isAddingNote={isCreateNotePending}
      isEditingNote={isUpdateNotePending}
    />
  );
}

// Independent techs and W2s have no business-information step; every other type does.
const TYPES_WITHOUT_BUSINESS_INFO = new Set(["independent_tech", "w2"]);

function AlignmentPage() {
  const { id, providerType } = Route.useParams();

  return (
    <Stack gap="var(--spacing-x-large)" w="100%">
      <AlignmentSection>
        <AlignmentInfoSection serviceProviderId={id} stepType="application-info" />
      </AlignmentSection>
      {!TYPES_WITHOUT_BUSINESS_INFO.has(providerType) && (
        <AlignmentSection title="Business Information">
          <AlignmentInfoSection serviceProviderId={id} stepType="business-information" />
        </AlignmentSection>
      )}
      <AlignmentSection>
        <Trades />
      </AlignmentSection>
      <AlignmentSection>
        <AlignmentRates id={id} providerType={providerType} />
      </AlignmentSection>
      <AlignmentSection>
        <Coverage />
      </AlignmentSection>
      <AlignmentSection>
        <AlignmentTabStep serviceProviderId={id} />
      </AlignmentSection>
      <AlignmentSection>
        <AlignmentRanking serviceProviderId={id} />
      </AlignmentSection>
      <AlignmentSection title="Internal Notes">
        <InternalNotesSection serviceProviderId={id} providerType={providerType} />
      </AlignmentSection>
    </Stack>
  );
}
